import { getCookie, retrieveSessionFromStorage } from '@/_helpers/queryInterceptorHelpers';
import posthog from '@/_services/posthog';
import type {
  CustomIdField,
  Login,
  LoginStatus,
  LoginStatusCode,
  LoginFailure,
  Session
} from '@/types/auth';
import type { BatchView } from '@/types/batch';

const LOGIN_STATUS_CODES: ReadonlySet<LoginStatusCode> = new Set<LoginStatusCode>([
 'AC', 'AP', 'AV', 'II', 'IP', 'IL', 'IE', 'IO', 'IB', 'IC', 'EL', 'ML', 'ME', 'MP', 'MO', 'MC', 'NV', 'NB', 'ER', 'PL'
] as const);

/**
 * Record a new login in PostHog.
 *
 * @param login - The newly created login object.
 * @param loginId - The existing login ID, if reloading an active session.
 */
export function captureLogin(login: Login, loginId: string | null): void {
  // Exit early if reloading an existing session or if the login lacks an authentication token
  if (loginId || !login?.token) return;

  posthog.capture('respondent_logged_in', {
    respondentId: login.loginCode.respondent,
  });
}

/**
 * Check whether to display an error message on the login screen.
 *
 * @param loginStatus - The login status object.
 * @returns `true` if an error message should be displayed; otherwise `false`.
 */
export function checkForErrorMessage(loginStatus: LoginStatus): boolean {
  const isStatusRepeated: boolean = loginStatus.previousStatusCodes.includes(loginStatus.currentStatusCode);
  const statusCodeStartsWithM: boolean = loginStatus.currentStatusCode.startsWith("M");
  const hasErrorMessage: boolean = !!loginStatus.errorMessage

  return hasErrorMessage && (!statusCodeStartsWithM || isStatusRepeated);
}

/**
 * Derive the effective login status code for the current render.
 *
 * Returns "PL" (page loading) when:
 * - No loginCode has been loaded yet,
 * - No previous login status transitions have occurred,
 * - And we have enough information (session, cookie, or URL login code) to attempt an automatic login.
 *
 * @param loginCodeId - The ID of the loaded loginCode, if any.
 * @param previousStatusCodes - Array of prior login status codes recorded during this session.
 * @param currentStatusCode - The current login status code from state.
 * @param hasSessionId - Whether a valid session ID exists in storage.
 * @param hasCookieLoginId - Whether a login ID cookie is present.
 * @param loginCodeFromUrl - Login code parsed from the URL query string, if present.
 *
 * @returns `"PL"` if an automatic login attempt should occur; otherwise the current status code.
 */
export function deriveLoginStatusCode(
  loginCodeId: string | null | undefined,
  previousStatusCodes: LoginStatusCode[],
  currentStatusCode: LoginStatusCode,
  hasSessionId: boolean,
  hasCookieLoginId: boolean,
  loginCodeFromUrl: string | null
): LoginStatusCode {
  const shouldAutoAttemptLogin: boolean =
    !loginCodeId &&
    previousStatusCodes.length === 0 &&
    (hasSessionId || hasCookieLoginId || !!loginCodeFromUrl);

  return shouldAutoAttemptLogin ? 'PL' : currentStatusCode;
}

/**
 * Retrieve the login ID. First searches in session storage, then cookies.
 *
 * @returns The login ID, or `null` if none exists.
 */
export function getLoginId(): string | null {
  const session: Session | null = retrieveSessionFromStorage();
  const cookieLoginId: string = getCookie("loginid");
  return session?.id || cookieLoginId || null;
}

/**
 * Determines the next page a respondent should be redirected to after login based on the retrieved batch.
 *
 * Redirect priority:
 * 1. Batch includes a welcome → navigate to `/welcome`.
 * 2. Exactly one material → navigate directly to that survey.
 * 3. Default → navigate to the batch overview page.
 *
 * @param batchView - The loaded BatchView containing batch configuration and materials.
 * @returns The route path the respondent should be redirected to.
 */
export function getNextPage(batchView: BatchView): string {
  if (batchView.hasWelcome) return '/welcome';

  if (batchView.materials.length === 1) {
    return `/surveys/${batchView.materials[0].slug}`;
  }

  return '/overview';
}

/**
 * Type guard that checks whether a value conforms to the `CustomIdField` shape.
 *
 * @param value - The value to validate.
 * @returns `true` if `value` is a valid `CustomIdField`; otherwise `false`.
 */
export function isCustomIdField(value: unknown): value is CustomIdField {
  if (!isRecord(value)) return false;

  return (
    typeof value.description === 'string' &&
    typeof value.issuer === 'string' &&
    typeof value.placeholder === 'string' &&
    typeof value.regex === 'string'
  );
}

/**
 * Type guard that checks whether a value is a non-null object.
 *
 * @param value - The value to test.
 * @returns `true` if `value` is an object (and not `null`); otherwise `false`.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Safely parse and normalize a login error payload returned from the API.
 *
 * It guarantees a fully normalized `LoginFailure` so callers do not need to perform additional type
 * checks or fallbacks.
 *
 * @param data - The raw `error.response?.data` value from Axios (unknown shape).
 * @param statusFallback - The fallback status code to use if `data.status` is missing or invalid.
 * @param messageFallback - The fallback description to use if `data.message` is missing or invalid.
 *
 * @returns A normalized login error payload with:
 * - `statusCode` guaranteed to be a valid `LoginStatusCode`
 * - `message` guaranteed to be a string
 * - `customIdField` either a validated `CustomIdField` or `null`
 */
export function parseLoginErrorPayload(
  data: unknown,
  statusFallback: LoginStatusCode,
  messageFallback: string
): LoginFailure {
  if (!isRecord(data)) {
    return { statusCode: statusFallback, message: messageFallback, customIdField: null };
  }

  const statusCode: LoginStatusCode = typeof data.status === 'string' && LOGIN_STATUS_CODES.has(data.status as LoginStatusCode)
    ? (data.status as LoginStatusCode)
    : statusFallback;

  const message: string = typeof data.message === 'string'
    ? data.message.trim()
    : messageFallback

  const customIdField: CustomIdField | null = isCustomIdField(data.customIdField)
    ? (data.customIdField as CustomIdField)
    : null;

  return {
    statusCode,
    message,
    customIdField,
  };
}

/**
 * Store session information when a login ID and JWT tokens are returned by the API.
 *
 * @param loginId - The login ID returned from the API.
 * @param accessToken - The access token returned from the API.
 * @param refreshToken - The refresh token returned from the API.
 * @returns void
 */
export function storeSessionInfo(loginId: string, accessToken: string | undefined, refreshToken: string | undefined): void {
  const hasCookieLoginId: boolean = !!getCookie("loginid");
  if (!loginId || !accessToken || !refreshToken || hasCookieLoginId) return;

  const session = {
    'id': loginId,
    'accessToken': accessToken,
    'refreshToken': refreshToken
  };
  const sessionJson = JSON.stringify(session);
  sessionStorage.setItem('session', sessionJson);
}

/**
 * Update the list of status codes, appending the current status code to the list of previous ones.
 *
 * @param loginStatus - The LoginStatus object for which to revise status codes.
 * @return A list of the unique status codes the respondent has received.
 */
export function updatePreviousStatusCodes(loginStatus: LoginStatus): LoginStatusCode[] {
  return [...new Set([...loginStatus.previousStatusCodes, loginStatus.currentStatusCode])];
}